import { findLocationByPlaceId } from './geoLocator';
import { AutocompletePredictionRestrictionTypes } from './types';
import { createAutocompletionRequest, getAutoCompleteService } from './api/placePredictions';
import { CANADA_COUNTRY_FILTER, US_AND_CANADA_COUNTRY_FILTER } from './googleSearchPredictions';
import { CountryCode, CountryCodeList } from 'types/countries';

export async function getPositionFromDescription(areaName: string, useUsListings = false) {
  const country = useUsListings ? US_AND_CANADA_COUNTRY_FILTER : CANADA_COUNTRY_FILTER;
  const predictions = await queryByGeocode(areaName, country);
  return positionFromPrediction(predictions[0]?.place_id);
}

/**
 * Uses Google Autocomplete without country restrictions, letting Google
 * disambiguate purely from the input text — like Google Maps search.
 * Use this when the input already contains enough context (e.g., "Florida, ON").
 */
export async function getPositionFromDescriptionUnrestricted(areaName: string) {
  const predictions = await queryByGeocodeUnrestricted(areaName);
  return positionFromPrediction(predictions[0]?.place_id);
}

export async function getPoiPositionFromDescription(areaName: string, useUsListings = false) {
  const country = useUsListings ? US_AND_CANADA_COUNTRY_FILTER : CANADA_COUNTRY_FILTER;
  const predictions = await queryByPoi(areaName, country);
  return positionFromPrediction(predictions[0]?.place_id);
}

function positionFromPrediction(placeId: string | undefined) {
  // No prediction means there is nothing to geocode. Calling the geocoder with
  // a missing place id is an invalid request, not an empty result.
  if (!placeId) return undefined;
  return findLocationByPlaceId(placeId);
}

function resolveCountry(input: string, country: readonly CountryCode[]) {
  const inputUpper = input.toUpperCase();
  if ((/USA$|US$|, USA|, US/).test(inputUpper) && !country.includes(CountryCodeList.UNITED_STATES)) {
    return US_AND_CANADA_COUNTRY_FILTER;
  }
  return country;
}

async function queryByGeocode(input: string, country: readonly CountryCode[]) {
  const autocompleteService = await getAutoCompleteService();
  if (!autocompleteService) throw new Error('AutocompleteService could not be created.');

  const resolvedCountry = resolveCountry(input, country);
  const request = await createAutocompletionRequest(input, AutocompletePredictionRestrictionTypes.GEOCODE, resolvedCountry);
  const response = await autocompleteService.getPlacePredictions(request);

  if (response?.predictions) {
    return response.predictions;
  }
  return [];
}

async function queryByGeocodeUnrestricted(input: string) {
  const autocompleteService = await getAutoCompleteService();
  if (!autocompleteService) throw new Error('AutocompleteService could not be created.');

  const response = await autocompleteService.getPlacePredictions({
    input,
    types: [AutocompletePredictionRestrictionTypes.GEOCODE],
  });

  if (response?.predictions) {
    return response.predictions;
  }
  return [];
}

async function queryByPoi(input: string, country: readonly CountryCode[]) {
  const autocompleteService = await getAutoCompleteService();
  if (!autocompleteService) throw new Error('AutocompleteService could not be created.');

  const resolvedCountry = resolveCountry(input, country);
  const request = await createAutocompletionRequest(input, AutocompletePredictionRestrictionTypes.POINT_OF_INTEREST, resolvedCountry);
  const response = await autocompleteService.getPlacePredictions(request);

  if (response?.predictions && response.predictions.length > 0) {
    return response.predictions;
  }

  return queryByGeocode(input, country);
}
